import React, { useMemo, useRef, useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { graphic } from 'echarts';
import type { ECharts } from 'echarts';
import { WidgetDataResult, WidgetChartConfig, WidgetClickPoint } from '../types';
import { dashboardPalette, dashboardSentimentPaletteMap, otherCategoryColor } from '../../../utils/colors';
import { mergeEChartsOption, isDateLike, formatDateCategory, formatNumber, escapeHtml } from './echartsUtils';
import { groupByKey, GroupedSeries } from './dataUtils';
import { max } from 'd3';

interface AxisBreakItem {
  start: number;
  end: number;
  gap?: string;
  isExpanded?: boolean;
}

interface ChartBarProps {
  data: WidgetDataResult;
  config: WidgetChartConfig;
  onPointClick?: (point: WidgetClickPoint) => void;
}

export const ChartBar: React.FC<ChartBarProps> = ({ data, config, onPointClick }) => {
  const mapping = config.columnMapping;
  const isStacked = config.stacked ?? false;
  const isVertical = (config.barDirection ?? 'horizontal') === 'vertical';
  const isLogScale = config.logScale ?? false;
  const brushEnabled = config.brushEnabled ?? false;
  const axisBreak = config.axisBreak;
  const dataZoomEnabled = config.dataZoom ?? false;

  const echartsRef = useRef<ReactECharts>(null);

  // Track current axis breaks — updated both by config and by interactive brushing
  const [currentBreaks, setCurrentBreaks] = useState<AxisBreakItem[]>(
    () => (axisBreak?.initialBreaks ?? []).map(b => ({ ...b, gap: b.gap ?? '2%' }))
  );

  // Reset breaks when config changes
  useEffect(() => {
    setCurrentBreaks(
      (axisBreak?.initialBreaks ?? []).map(b => ({ ...b, gap: b.gap ?? '2%' }))
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(axisBreak?.initialBreaks)]);

  // Keep a ref so event handlers always see the latest value without stale closure
  const currentBreaksRef = useRef<AxisBreakItem[]>(currentBreaks);
  useEffect(() => { currentBreaksRef.current = currentBreaks; }, [currentBreaks]);

  // ZRenderer brush interaction — lets users drag to create axis breaks
  useEffect(() => {
    if (!axisBreak) return;
    const chart = echartsRef.current?.getEchartsInstance() as ECharts | undefined;
    if (!chart) return;

    const zr = chart.getZr();
    let brushingEl: InstanceType<typeof graphic.Rect> | null = null;

    const onMouseDown = (params: any) => {
      brushingEl = new graphic.Rect({
        shape: { x: 0, y: params.offsetY, width: 0, height: 0 },
        style: { stroke: 'none', fill: 'rgba(200,200,200,0.45)' },
        ignore: true,
        silent: true,
      });
      zr.add(brushingEl);
    };

    const onMouseMove = (params: any) => {
      if (!brushingEl) return;
      const initY = (brushingEl.shape as any).y as number;
      brushingEl.setShape({ width: chart.getWidth(), height: params.offsetY - initY });
      brushingEl.ignore = false;
    };

    const onMouseUp = (e: MouseEvent) => {
      if (!brushingEl) return;
      const initY = (brushingEl.shape as any).y as number;
      const pixelSpan = Math.abs(e.offsetY - initY);
      if (pixelSpan > 2) {
        updateAxisBreak([0, initY], [e.offsetX, e.offsetY]);
      }
      zr.remove(brushingEl);
      brushingEl = null;
    };

    function updateAxisBreak(initXY: number[], currPoint: number[]) {
      const dataXY0 = chart!.convertFromPixel({ gridIndex: 0 }, initXY);
      const dataXY1 = chart!.convertFromPixel({ gridIndex: 0 }, currPoint);
      const dataRange = [Math.round(dataXY0[1]), Math.round(dataXY1[1])];
      if (dataRange[0] > dataRange[1]) dataRange.reverse();

      const newBreak: AxisBreakItem = { start: dataRange[0], end: dataRange[1] };
      const merged = insertAndMerge(newBreak, [...currentBreaksRef.current]);

      const pixelSpanPct = (Math.abs(initXY[1] - currPoint[1]) / (chart!.getHeight() * 0.75) * 100).toFixed(1) + '%';

      function applyBreaks(gap: string) {
        const breaks = merged.map(b =>
          b.start === newBreak.start && b.end === newBreak.end ? { ...b, gap } : b
        );
        chart!.setOption({ yAxis: { breaks } });
        setCurrentBreaks(breaks);
        currentBreaksRef.current = breaks;
      }

      applyBreaks(pixelSpanPct);
      setTimeout(() => applyBreaks('2%'), 0);
    }

    function insertAndMerge(newBreak: AxisBreakItem, existing: AxisBreakItem[]): AxisBreakItem[] {
      for (let i = existing.length - 1; i >= 0; i--) {
        const b = existing[i];
        if ((b.start as number) <= (newBreak.end as number) && (b.end as number) >= (newBreak.start as number)) {
          newBreak.start = Math.min(b.start as number, newBreak.start as number);
          newBreak.end = Math.max(b.end as number, newBreak.end as number);
          existing.splice(i, 1);
        }
      }
      existing.push(newBreak);
      return existing;
    }

    const onAxisBreakChanged = (params: any) => {
      const changedBreaks: AxisBreakItem[] = params.breaks ?? [];
      const filtered = currentBreaksRef.current.filter(b =>
        !changedBreaks.some(c => c.isExpanded && c.start === b.start && c.end === b.end)
      );
      setCurrentBreaks(filtered);
      currentBreaksRef.current = filtered;
    };

    zr.on('mousedown', onMouseDown);
    zr.on('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    chart.on('axisbreakchanged', onAxisBreakChanged);

    return () => {
      zr.off('mousedown', onMouseDown);
      zr.off('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      chart.off('axisbreakchanged', onAxisBreakChanged);
      if (brushingEl) { zr.remove(brushingEl); brushingEl = null; }
    };
  // Re-run only when axisBreak is toggled on/off, not on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!axisBreak]);

  const { categories, seriesData, rowLookup, isMultiSeries } = useMemo(() => {
    // Key-based grouping mode
    if (mapping?.key && mapping?.xAxis && mapping?.yAxis) {
      const grouped: GroupedSeries = groupByKey(
        data.rows, mapping.xAxis, mapping.yAxis, mapping.key, mapping.label,
      );
      return {
        categories: grouped.categories,
        seriesData: grouped.series,
        rowLookup: grouped.rowLookup,
        isMultiSeries: grouped.series.length > 1,
      };
    }

    // Mapped single/multi-column mode (no key grouping)
    const xCol = mapping?.xAxis || data.columns[0];
    const valueCols = mapping?.yAxis
      ? [mapping.yAxis]
      : (data.columns.length > 1 ? data.columns.slice(1) : [data.columns[0]]);

    const cats = data.rows.map((row) => String(row[xCol] ?? ''));

    const lookup = new Map<string, Map<string, typeof data.rows[0]>>();
    data.rows.forEach((row, i) => {
      const cat = cats[i];
      if (!lookup.has(cat)) lookup.set(cat, new Map());
      for (const col of valueCols) {
        lookup.get(cat)!.set(col, row);
      }
    });

    return {
      categories: cats,
      seriesData: valueCols.map((col) => ({
        name: col,
        data: data.rows.map((row) => Number(row[col]) || 0),
      })),
      rowLookup: lookup,
      isMultiSeries: valueCols.length > 1,
    };
  }, [data, mapping]);

  const colors = config.colors?.length
    ? config.colors
    : seriesData.map((s, i) => {
        const key = s.name.toUpperCase();
        if (key === 'OTHER') return otherCategoryColor;
        if (config.useSentimentColors) {
          return dashboardSentimentPaletteMap[key as keyof typeof dashboardSentimentPaletteMap] ?? dashboardPalette[i % dashboardPalette.length];
        }
        return dashboardPalette[i % dashboardPalette.length];
      });

  const legendPosition = config.legend?.position ?? (isMultiSeries ? 'bottom' : 'none');
  const legendConfig = useMemo(() => {
    const cfg: Record<string, unknown> = {
      type: 'scroll',
      show: legendPosition !== 'none',
      icon: 'circle',
      textStyle: { fontSize: 12 },
    };
    if (legendPosition === 'top') cfg.top = 0;
    else if (legendPosition === 'bottom') cfg.bottom = 0;
    else if (legendPosition === 'left') { cfg.left = 0; cfg.orient = 'vertical'; }
    else if (legendPosition === 'right') { cfg.right = 0; cfg.orient = 'vertical'; }
    return cfg;
  }, [legendPosition]);

  const hasDateCategories = categories.length > 0 && isDateLike(categories[0]);
  const showDataZoom = dataZoomEnabled && !isStacked;

  const option = useMemo(() => {
    const stackKey = isStacked ? 'total' : undefined;

    const categoryTotals = isStacked
      ? categories.map((_, ci) => seriesData.reduce((sum, s) => sum + (s.data[ci] || 0), 0))
      : [];

    const labelPosition = isMultiSeries
      ? 'inside'
      : (isVertical ? 'top' : 'right');

    const series = seriesData.map((s) => ({
      name: s.name,
      type: 'bar' as const,
      stack: stackKey,
      data: isStacked
        ? s.data.map((val, ci) => ({
            value: val,
            label: {
              show: categoryTotals[ci] > 0 && val / categoryTotals[ci] >= 0.1,
            },
          }))
        : s.data,
      barMaxWidth: isMultiSeries ? '60%' : '50%',
      barMinWidth: 20,
      label: {
        show: true,
        hideOverlap: true,
        position: labelPosition as string,
        fontSize: 11,
        fontWeight: 'bold' as const,
        color: isMultiSeries ? '#fff' : '#151419',
        overflow: 'truncate' as const,
        formatter: (params: any) => {
          const val = typeof params === 'object' ? params.value : params;
          return typeof val === 'number' ? formatNumber(val) : String(val ?? '');
        },
      },
    }));

    // Category axis config
    const categoryAxisConfig: Record<string, unknown> = {
      type: 'category' as const,
      data: categories,
      axisLabel: {
        fontSize: 12,
        color: '#4F546C',
        rotate: isVertical && categories.length > 6 ? 30 : 0,
        overflow: 'truncate' as const,
        ...(hasDateCategories && { formatter: formatDateCategory }),
      },
    };

    // Value axis config — includes breaks when axisBreak is enabled
    const valueAxisConfig: Record<string, unknown> = {
      type: isLogScale ? 'log' : 'value',
      ...(isLogScale && { logBase: 10 }),
      axisLabel: { fontSize: 12, color: '#666', formatter: (val: number) => formatNumber(val) },
      splitLine: { show: config.showGrid !== false, lineStyle: { color: '#f0f0f0' } },
      ...(axisBreak && {
        breaks: currentBreaks,
        breakArea: {
          itemStyle: { opacity: 1 },
          zigzagMaxSpan: 15,
          zigzagAmplitude: 2,
          zigzagZ: 200,
        },
      }),
    };

    const rawMax = isStacked
      ? (max(categoryTotals) ?? 0)
      : Math.max(0, ...seriesData.flatMap((s) => s.data));
    if (rawMax > 0 && !isLogScale) valueAxisConfig.max = rawMax + rawMax / 10;

    // Assign axis names based on orientation
    if (isVertical) {
      categoryAxisConfig.name = config.labels?.x || '';
      categoryAxisConfig.nameLocation = 'end';
      categoryAxisConfig.nameGap = 8;
      valueAxisConfig.name = config.labels?.y || '';
    } else {
      categoryAxisConfig.name = config.labels?.y || '';
      valueAxisConfig.name = config.labels?.x || '';
    }

    const zoomEnd = showDataZoom ? Math.round((20 / categories.length) * 100) : 100;
    const dataZoom = showDataZoom
      ? [
          {
            type: 'slider' as const,
            [isVertical ? 'xAxisIndex' : 'yAxisIndex']: 0,
            filterMode: 'filter' as const,
            start: 0,
            end: zoomEnd,
            height: isVertical ? 18 : undefined,
            width: !isVertical ? 18 : undefined,
            handleSize: '80%',
            showDetail: false,
          },
          { type: 'inside' as const, [isVertical ? 'xAxisIndex' : 'yAxisIndex']: 0, filterMode: 'filter' as const },
        ]
      : undefined;

    const toolbox = brushEnabled
      ? {
          top: 4,
          right: 8,
          feature: {
            dataZoom: {
              yAxisIndex: isVertical ? 0 : 'none',
              xAxisIndex: isVertical ? 'none' : 0,
              title: { zoom: 'Zoom', back: 'Reset zoom' },
            },
            restore: { title: 'Reset' },
          },
        }
      : undefined;

    const brush = brushEnabled
      ? {
          toolbox: [isVertical ? 'lineX' : 'lineY', 'clear'],
          brushStyle: {
            borderWidth: 1,
            color: 'rgba(37, 99, 235, 0.06)',
            borderColor: 'rgba(37, 99, 235, 0.35)',
          },
        }
      : undefined;

    const baseTop = (legendPosition === 'top' ? 36 : 0) + (brushEnabled ? 28 : 0) + 8;

    const base = {
      color: colors,
      tooltip: {
        trigger: 'axis' as const,
        axisPointer: { type: 'shadow' as const },
        appendToBody: true,
        formatter: (params: any) => {
          const title = escapeHtml(params[0]?.axisValueLabel ?? params[0]?.name ?? '');
          const lines = (params as any[]).map((p: any) => {
            const val = typeof p.value === 'number' ? formatNumber(p.value) : escapeHtml(p.value);
            return `${p.marker}${escapeHtml(p.seriesName)}: <b>${val}</b>`;
          });
          return [title, ...lines].join('<br/>');
        },
      },
      legend: legendConfig,
      grid: isVertical
        ? {
            left: 10,
            right: 10,
            top: baseTop,
            bottom: (legendPosition === 'bottom' ? 50 : 20) + (showDataZoom ? 30 : 0),
            containLabel: true,
          }
        : {
            left: 100,
            right: showDataZoom ? 36 : 40,
            top: baseTop,
            bottom: legendPosition === 'bottom' ? 50 : 30,
            containLabel: false,
          },
      xAxis: isVertical ? categoryAxisConfig : valueAxisConfig,
      yAxis: isVertical ? valueAxisConfig : categoryAxisConfig,
      ...(toolbox && { toolbox }),
      ...(brush && { brush }),
      ...(dataZoom && { dataZoom }),
      series,
    };
    return mergeEChartsOption(base, config.echartsOption);
  }, [categories, seriesData, colors, currentBreaks, isMultiSeries, isStacked, isVertical, isLogScale, brushEnabled, axisBreak, legendConfig, legendPosition, hasDateCategories, config, showDataZoom]);

  const onEvents = useMemo(() => {
    if (!onPointClick) return undefined;
    return {
      click: (params: any) => {
        const cat = String(params.name ?? '');
        const seriesName = String(params.seriesName ?? params.name ?? '');
        const row = rowLookup.get(cat)?.get(seriesName);
        onPointClick({
          label: seriesName,
          key: cat,
          value: params.value ?? 0,
          extra: row ? { ...row } as Record<string, string | number | boolean | null> : undefined,
        });
      },
    };
  }, [onPointClick, rowLookup]);

  return (
    <ReactECharts
      ref={echartsRef}
      option={option}
      style={{ width: '100%', height: '100%' }}
      opts={{ renderer: 'svg' }}
      onEvents={onEvents}
    />
  );
};
